﻿import {gsap} from "gsap";
import {EntityView} from "./EntityView";
import {Core} from "../../Base/Core";
import {MoneyType, UIState} from "../../enums";
import {GameManager, TUTORIAL_STATE} from "../GameManager";
import {Random} from "../../Utils/Unity/Random";
import {Time} from "../../Utils/Unity/Time";
import {ClickBonusUI} from "../../UI/Tools/ClickBonusUI";
import {UIText} from "../../UI/Tools/Base/UIText";

export class CreatureView extends EntityView {

    Awake() {
        super.Awake();
        this.levelField = this.addChild(new UIText(""));
        this.creatureController = null; // CreatureController
        this.timeFromPreviousClick = 0;

        this.stayTime = 0;
        this._walking = null; // gsap.Tween
        this.isMerging = false;
        this.inBox = false;
        this.initTime = 0;
    }

    Init(creatureController, inBox) {
        this.onCreatureRemoved = this.onCreatureRemoved.bind(this);
        this.initTime = Time.time;
        this.creatureController = creatureController;
        this.SetBox(inBox);

        this.creatureController.on("CreatureRemoved", this.onCreatureRemoved);

        this.timeFromPreviousClick = Random.Range(-3, 3);
        this.stayTime = Random.Range(3, 10);
        this._walking = null;

        this.pointerOffset = new PIXI.Point(0, 0);

        this.levelField.text = "";
    }

    SetBox(val) {
        this.inBox = val;
        if (!this.inBox) {
            this.sprite.setImage("creatures/" + this.creatureController.creatureId);
            this.levelField.y = this.sprite.height * 0.5;
            this.scale.set(0.7);
        } else {
            this.sprite.setImage("box");
            this.scale.set(1);
        }
    }

    SetLevel(level) {
        this.levelField.text = level;
    }

    onCreatureRemoved() {
        this.creatureController.off("CreatureRemoved", this.onCreatureRemoved);
        this.creatureController = null;
        GameManager.gameView.RemoveCreatureView(this);
    }

    _startWalking() {
        if (GameManager.tutorialState < TUTORIAL_STATE.MERGED) return this._stopWalking();
        let p = GameManager.GetRandomPointInCameraView();
        this._walking = gsap.to(this, {
            x: p.x, y: p.y,
            duration: 2,
            ease: "power5.out",
            onComplete: () => {
                this._stopWalking();
            },
            onInterrupt: () => {
                this._stopWalking();
            }
        });
    }

    _stopWalking() {
        if (this._walking && this._walking.isActive()) {
            this._walking.kill();
        }
        this._walking = null;
        this.stayTime = Random.Range(4, 7);
    }

    Idle() {
        gsap.timeline()
            .to(this.sprite.scale, {y: 0.8, duration: 0.1, ease: "power3.in"}, "+=0")
            .to(this.sprite.scale, {y: 1, duration: 0.7, ease: "elastic.out"}, "+=0")
        ;
    }

    Update() {
        super.Update();

        if (this.inBox || this.isMerging) return;

        this.timeFromPreviousClick += Time.deltaTime;
        if (this.timeFromPreviousClick > 5) {
            this.click(true);
        }

        if (this.pointerId !== null) return;

        if (this._walking && !this._walking.isActive()) {
            this._stopWalking();
        }

        if (!this._walking) {
            this.stayTime -= Time.deltaTime;
            if (this.stayTime <= 0) {
                this._startWalking();
            }
        }
    }

    OpenBox() {
        if (!this.creatureController) return;
        Core.Audio.Play("BoxOpen");
        Core.Vibro.Vibrate();
        this.SetBox(false);
        Core.Player.PlanetsSave.CurrentPlanet.RemoveBoxCreature(this.creatureController.creatureData.id);
        this.Idle();
    }

    canInteract(p = null) {
        if (!super.canInteract(p)) return false;
        return this.inBox || !this.isMerging;
    }

    mouseDown(p) {
        if (Core.UI.state != UIState.Game) return;
        if (Time.time - this.initTime < 0.1) return;
        if (this.isMerging) return;

        this._stopWalking();

        let {resolution} = Core.app.renderer;
        this.pointerOffset.set(
            p.x * resolution - this.x,
            p.y * resolution - this.y
        );

        if (this.inBox) {
            return this.OpenBox();
        }

        this.click(false, p);
    }

    mouseMove(p) {
        if (!this.creatureController || this.inBox) return;
        if (Core.UI.state != UIState.Game) return;
        if (Time.time - this.initTime < 0.1) return;

        let {resolution} = Core.app.renderer;
        this.position.set(
            Math.max(0, Math.min(Core.app.viewport.width, p.x * resolution - this.pointerOffset.x)),
            Math.max(0, Math.min(Core.app.viewport.height, p.y * resolution - this.pointerOffset.y))
        );
    }

    mouseUp(p) {
        if (!this.creatureController || this.inBox) return;
        if (Core.UI.state != UIState.Game) return;
        if (Time.time - this.initTime < 0.1) return;
        this.tryMerge();
    }

    click(autoClick) {
        if (!this.creatureController || this.inBox || this.isMerging) return;

        let bonus = this.creatureController.creatureData.GetSoftPerSecond();

        if (Random.Range(0, 1) < 0.2) {
            let multiplayer = Random.Range(2, 4);
            bonus *= BigInt(~~multiplayer);
        }

        if (Core.Boosters.IsIncome2Active) {
            bonus *= 4n;
        } else if (Core.Boosters.IsIncomeActive) {
            bonus *= 2n;
        }

        bonus *= BigInt(~~Core.GameData.Planets.GetPlanet(Core.Player.PlanetsSave.CurrentPlanet.planetId).tapIncomeMultiplayer);

        Core.Player.AddMoney(MoneyType.Soft, bonus);

        if (!autoClick) {
            GameManager.emit("CreatureClick", this.creatureController, bonus);
        }

        if (this.Active) {
            if (GameManager.TapChallengeMode.IsActive) {
                ClickBonusUI.ShowTapChallenge(this.position);
            } else {
                ClickBonusUI.ShowCoins(bonus, this.position);
            }
        }

        if (!autoClick && GameManager.TapChallengeMode.IsActive) {
            Core.UI.tapChallenge.RunTapParticles(this.position);
        }

        this.Idle();

        if (!Core.UI.IsSomethingFullScreenOpened) {
            if (autoClick) {
                if (this.Active) Core.Audio.Play("PassiveCoin");
            } else {
                Core.Audio.Play("CreatureClick");
            }
        }

        this.timeFromPreviousClick = 0;

        if (!autoClick) {
            this._stopWalking();
        }

    }

    spawnMergeParticles() {
        Core.VFX.creatureMerge(this.x, this.y);
    }

    canMerge(otherCreature = null, checkPosition = false) {
        if (this.inBox || this.isMerging) return false;
        if (!otherCreature) return true;
        if (!this.creatureController || !otherCreature.creatureController) return false;
        if (this.creatureController === otherCreature.creatureController) return false;
        if (this.creatureController.creatureId !== otherCreature.creatureController.creatureId) return false;
        if (checkPosition) {
            return this.getBounds().intersects(otherCreature.getBounds());
        }
        return true;
    }

    tryMerge(creatureForMerge = null, ignorePosition = false) {
        if (GameManager.TapChallengeMode.IsInProgress) return false;
        if (this.isMerging) return false;
        if (this.creatureController.creatureData.IsFinalForm) {
            return false;
        }

        if (!creatureForMerge) {
            creatureForMerge = GameManager.gameView.GetCreatureForMerge(this, ignorePosition);
        }

        if (!creatureForMerge) return false;

        let currentEpoch = this.creatureController.epoch.epochData;
        let creatureIndex = currentEpoch.creatures.indexOf(this.creatureController.creatureId);

        // merge inside the same epoch
        if (creatureIndex < currentEpoch.creatures.length - 1) {
            let nextCreatureId = currentEpoch.creatures[creatureIndex + 1];
            this.mergeCreatures(creatureForMerge, nextCreatureId);
            return true;
        }

        // merge to the next epoch
        let nextEpochs = this.creatureController.epoch.epochData.nextEpochs
            .filter(id => Core.Player.PlanetsSave.EpochCanBeChosen(id))
        ;
        if (!nextEpochs.length) {
            // All next epochs are opened, find selected next epoch
            let nextEpochId = this.creatureController.epoch.epochData.nextEpochs.find(id => Core.Player.PlanetsSave.CurrentPlanet.epochs.includes(id));
            if (!nextEpochId) return false;
            this.epochSelected(creatureForMerge, nextEpochId);
            return true;
        }

        if (nextEpochs.length == 1) {
            // there is no choice, only a single next epoch is available
            this.epochSelected(creatureForMerge, nextEpochs[0]);
            return true;
        }

        // multiple epochs available
        let nextEpoch = nextEpochs.find(e => Core.Player.PlanetsSave.CurrentPlanet.epochs.includes(e));
        if (nextEpoch !== undefined) {
            // next epoch has already chosen
            this.epochSelected(creatureForMerge, nextEpoch);
            return true;
        }

        Core.UI.nextEpochSelection.Show((selectedEpochIndex) => {
            this.epochSelected(creatureForMerge, nextEpochs[selectedEpochIndex]);
        }, nextEpochs.map(id => Core.GameData.Epochs.GetById(id)));
        return true;
    }

    mergeCreatures(creatureForMerge, nextCreatureId) {
        if (!GameManager.gameController.EnoughSpaceForCreature(nextCreatureId, this.creatureController.creatureData.id)) {
            let epochId = Core.GameData.Epochs.GetByCreatureId(nextCreatureId).id;
            Core.UI.noSpace.Show(epochId);
            return false;
        }

        this.isMerging = true;
        this._stopWalking();
        creatureForMerge.isMerging = true;
        creatureForMerge._stopWalking();

        let dx = this.x - creatureForMerge.x;
        let dy = this.y - creatureForMerge.y;
        let mergePoint = new PIXI.Point(this.x - dx * 0.5, this.y - dy * 0.5);

        gsap.to([this, creatureForMerge], {
            x: mergePoint.x,
            y: mergePoint.y,
            duration: 0.3,
            ease: "power3.out",
            onComplete: () => {
                if (this.creatureController.epoch.epochData.creatures.indexOf(this.creatureController.creatureData.id) == this.creatureController.epoch.epochData.creatures.length - 1) {
                    Core.UI.lastMonsterAnimation.Show(nextCreatureId, this.position);
                }

                this.creatureController.RemoveCreature();
                creatureForMerge.creatureController.RemoveCreature();
                GameManager.gameController.AddCreature(nextCreatureId, this.position, false, true, true);
                Core.Audio.Play("CreatureMerge");
                Core.Vibro.Vibrate();

                Core.Player.PlanetsSave.CurrentPlanet.AddMergeProgress();

                this.spawnMergeParticles();

                if (GameManager.tutorialState == 5) {
                    Core.Analytics.SendTutorialEvent(5);
                    GameManager.tutorialState++;
                }
            }
        });
        Core.Player.PlanetsSave.TutorSave.merged = true;
        return true;
    }

    epochSelected(creatureForMerge, nextEpochId) {
        let epochData = Core.GameData.Epochs.GetById(nextEpochId);
        if (this.mergeCreatures(creatureForMerge, epochData.creatures[0])) {
            if (Core.Player.PlanetsSave.CurrentPlanet.epochs.includes(nextEpochId)) {
                Core.VFX.newCreatureNextEpoch(this, epochData);
            }
            else {
                Core.Player.PlanetsSave.CurrentPlanet.AddEpoch(nextEpochId);
            }
        }
    }

    Evolve(creatureId) {
        if (this.creatureController.epoch.epochData.creatures.indexOf(this.creatureController.creatureData.id) == this.creatureController.epoch.epochData.creatures.length - 1) {
            Core.UI.lastMonsterAnimation.Show(creatureId, this.t.position);
        }

        this.creatureController.RemoveCreature();
        GameManager.gameController.AddCreature(creatureId, this.position, false, true, false);
        Core.Audio.Play("CreatureMerge");

        Core.Player.PlanetsSave.CurrentPlanet.AddMergeProgress();
    }
}
